import type { WorkItemListItem } from '@vwt/contracts';

export const GROUP_FIELDS = ['phase', 'priority', 'assignee', 'project', 'team', 'type', 'label'] as const;
export type GroupField = (typeof GROUP_FIELDS)[number];
export const LANE_FIELDS = ['assignee', 'priority', 'project', 'type'] as const;
export type LaneField = (typeof LANE_FIELDS)[number];

const PHASES = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'INTEGRATED', 'DONE', 'CANCELED'];
const PRIORITIES = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];
const TYPES = ['EPIC', 'FEATURE', 'BUG', 'MAINTENANCE', 'INFRASTRUCTURE', 'RESEARCH', 'ANALYSIS', 'OPERATIONS', 'REVIEW', 'OTHER'];

export interface Group {
  /** Stable key; "" is the empty group (no assignee, no project, no label…). */
  key: string;
  /** Translation key for fixed values, or the display text for people, projects, teams and labels. */
  label: { i18n: string } | { text: string };
  items: WorkItemListItem[];
}

function keysOf(item: WorkItemListItem, field: GroupField): Array<{ key: string; label: Group['label'] }> {
  switch (field) {
    case 'phase':
      return [{ key: item.phase, label: { i18n: `status.phase.${item.phase}` } }];
    case 'priority':
      return [{ key: item.priority, label: { i18n: `status.priority.${item.priority}` } }];
    case 'type':
      return [{ key: item.type, label: { i18n: `status.workType.${item.type}` } }];
    case 'assignee':
      return [item.assignee ? { key: item.assignee.id, label: { text: item.assignee.displayName } } : { key: '', label: { i18n: 'common.unassigned' } }];
    case 'project':
      return [item.project ? { key: item.project.id, label: { text: item.project.name } } : { key: '', label: { i18n: 'work.groups.noProject' } }];
    case 'team':
      return [item.team ? { key: item.team.id, label: { text: item.team.name } } : { key: '', label: { i18n: 'work.groups.noTeam' } }];
    case 'label':
      // An item with several labels appears under each of them.
      return item.labels.length ? item.labels.map((l) => ({ key: l, label: { text: l } })) : [{ key: '', label: { i18n: 'work.groups.noLabel' } }];
  }
}

function order(field: GroupField, key: string, label: Group['label']): [number, string] {
  const fixed = field === 'phase' ? PHASES : field === 'priority' ? PRIORITIES : field === 'type' ? TYPES : null;
  if (fixed) return [fixed.indexOf(key), ''];
  // Named groups alphabetically, the empty group last.
  return [key === '' ? 1 : 0, 'text' in label ? label.text.toLocaleLowerCase() : ''];
}

/** Groups loaded items client-side (the list loads in pages, so groups cover the loaded pages). */
export function groupItems(items: WorkItemListItem[], field: GroupField): Group[] {
  const groups = new Map<string, Group>();
  for (const item of items) {
    for (const { key, label } of keysOf(item, field)) {
      const group = groups.get(key) ?? { key, label, items: [] };
      group.items.push(item);
      groups.set(key, group);
    }
  }
  return [...groups.values()].sort((a, b) => {
    const [ra, ta] = order(field, a.key, a.label);
    const [rb, tb] = order(field, b.key, b.label);
    return ra - rb || ta.localeCompare(tb);
  });
}

export const isGroupField = (value: string | null): value is GroupField => !!value && (GROUP_FIELDS as readonly string[]).includes(value);
export const isLaneField = (value: string | null): value is LaneField => !!value && (LANE_FIELDS as readonly string[]).includes(value);
